/**
 * Ground routing over the authored taxi network.
 *
 * Given a `TaxiPlan`, resolve the subset of nodes/edges live at a leg's time
 * (`resolveNetworkAt` — applies group/window/closure gating), then A* from the
 * node nearest `from` to the node nearest `to`. The returned polyline is
 * `[from, ...nodePath, to]`; the call site smooths it with `smoothPath`.
 *
 * Because routing is evaluated per leg at that leg's own time, a scheduled
 * closure affects only aircraft taxiing during it, and a real-time emergency
 * close (an edit to the plan) reroutes affected aircraft when `buildAllPlans`
 * re-runs — all while routes stay a pure function of (plan, time).
 *
 * Falls back to the single-spine `routeTaxi` heuristic when the network is
 * empty or the endpoints can't be connected, so the demo is always runnable.
 */
import type { FieldGraph } from '../data/fieldGraph'
import { resolveNetworkAt, type NodeRole, type TaxiNode, type TaxiPlan } from '../data/taxiGraph'
import type { Pt } from './easing'
import { routeTaxi } from './routeTaxi'

const dist = (a: readonly [number, number], b: readonly [number, number]) =>
  Math.hypot(a[0] - b[0], a[1] - b[1])

interface Adjacency {
  nodeById: Map<string, TaxiNode>
  /** nodeId → list of { to, cost } reachable via a live edge */
  neighbors: Map<string, Array<{ to: string; cost: number }>>
}

/** Build a directed adjacency map from the live network at `atMs`. */
export function buildAdjacency(plan: TaxiPlan, atMs: number): Adjacency {
  const { nodes, edges } = resolveNetworkAt(plan, atMs)
  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const neighbors = new Map<string, Array<{ to: string; cost: number }>>()
  for (const n of nodes) neighbors.set(n.id, [])

  for (const e of edges) {
    const na = nodeById.get(e.a)
    const nb = nodeById.get(e.b)
    if (!na || !nb) continue
    const cost = dist(na.pos, nb.pos) * (e.weightMult && e.weightMult > 0 ? e.weightMult : 1)
    neighbors.get(e.a)!.push({ to: e.b, cost })
    if (!e.directed) neighbors.get(e.b)!.push({ to: e.a, cost })
  }
  return { nodeById, neighbors }
}

/** Nearest live node id to a point, or null if the network is empty. */
function nearestNodeId(adj: Adjacency, p: Pt): string | null {
  let best: string | null = null
  let bestD = Infinity
  for (const [id, n] of adj.nodeById) {
    const d = dist(n.pos, p)
    if (d < bestD) {
      bestD = d
      best = id
    }
  }
  return best
}

/** Nearest live node id whose role is in `roles`, or null if none exist. */
function nearestNodeIdOfRole(adj: Adjacency, p: Pt, roles: readonly NodeRole[]): string | null {
  let best: string | null = null
  let bestD = Infinity
  for (const [id, n] of adj.nodeById) {
    if (!roles.includes(n.role)) continue
    const d = dist(n.pos, p)
    if (d < bestD) {
      bestD = d
      best = id
    }
  }
  return best
}

/** A* over the adjacency; returns the node-id path inclusive of start/goal, or null. */
function aStar(adj: Adjacency, startId: string, goalId: string): string[] | null {
  if (startId === goalId) return [startId]
  const goal = adj.nodeById.get(goalId)
  if (!goal) return null
  const h = (id: string) => {
    const n = adj.nodeById.get(id)
    return n ? dist(n.pos, goal.pos) : 0
  }

  const open = new Set<string>([startId])
  const cameFrom = new Map<string, string>()
  const g = new Map<string, number>([[startId, 0]])
  const f = new Map<string, number>([[startId, h(startId)]])

  while (open.size > 0) {
    // Pop the open node with the lowest f (small graph → linear scan is fine).
    let current: string | null = null
    let bestF = Infinity
    for (const id of open) {
      const fv = f.get(id) ?? Infinity
      if (fv < bestF) {
        bestF = fv
        current = id
      }
    }
    if (current === null) break
    if (current === goalId) {
      const path = [current]
      let c = current
      while (cameFrom.has(c)) {
        c = cameFrom.get(c)!
        path.push(c)
      }
      return path.reverse()
    }
    open.delete(current)
    for (const { to, cost } of adj.neighbors.get(current) ?? []) {
      const tentative = (g.get(current) ?? Infinity) + cost
      if (tentative < (g.get(to) ?? Infinity)) {
        cameFrom.set(to, current)
        g.set(to, tentative)
        f.set(to, tentative + h(to))
        open.add(to)
      }
    }
  }
  return null
}

/**
 * Endpoint role preferences. When set, the matching endpoint snaps to the
 * nearest node of one of these roles instead of the globally-nearest node — so a
 * route leaving the runway is pinned to an authored runway exit (not a diagonal
 * shortcut to whatever taxiway node happens to be closest). Falls back to the
 * globally-nearest node when no node of the preferred role is live.
 */
export interface RouteGroundOpts {
  /** Preferred roles for the start node (e.g. runway exits on a landing rollout). */
  entryRoles?: readonly NodeRole[]
  /** Preferred roles for the goal node (e.g. hold-short before a takeoff roll). */
  goalRoles?: readonly NodeRole[]
}

/**
 * Route a ground move from `from` to `to` (scene feet) through the authored
 * network as it stands at `atMs`. Falls back to `routeTaxi(field, …)` when no
 * usable network/path exists.
 */
export function routeGround(
  plan: TaxiPlan | null | undefined,
  from: Pt,
  to: Pt,
  atMs: number,
  field?: FieldGraph | null,
  opts?: RouteGroundOpts
): Pt[] {
  const fallback = (): Pt[] =>
    field ? routeTaxi(field, from, to) : [[from[0], from[1]], [to[0], to[1]]]

  if (!plan || plan.nodes.length === 0) return fallback()

  const adj = buildAdjacency(plan, atMs)
  if (adj.nodeById.size === 0) return fallback()

  const startId =
    (opts?.entryRoles && nearestNodeIdOfRole(adj, from, opts.entryRoles)) || nearestNodeId(adj, from)
  const goalId =
    (opts?.goalRoles && nearestNodeIdOfRole(adj, to, opts.goalRoles)) || nearestNodeId(adj, to)
  if (!startId || !goalId) return fallback()

  const nodePath = aStar(adj, startId, goalId)
  if (!nodePath || nodePath.length === 0) return fallback()

  const out: Pt[] = [[from[0], from[1]]]
  for (const id of nodePath) {
    const n = adj.nodeById.get(id)
    if (n) out.push([n.pos[0], n.pos[1]])
  }
  out.push([to[0], to[1]])
  return out
}

/**
 * Which exit_to_taxiway node a departure routes through at `atMs` — the live
 * exit node nearest the runway-bound endpoint. Used for the departure
 * distribution readout. Returns null when there are no live exit nodes.
 */
export function nearestExitNode(plan: TaxiPlan | null | undefined, towardRunway: Pt, atMs: number): TaxiNode | null {
  if (!plan) return null
  const { nodes } = resolveNetworkAt(plan, atMs)
  let best: TaxiNode | null = null
  let bestD = Infinity
  for (const n of nodes) {
    if (n.role !== 'exit_to_taxiway') continue
    const d = dist(n.pos, towardRunway)
    if (d < bestD) {
      bestD = d
      best = n
    }
  }
  return best
}
